import { PolicyGetResponse, PolicySearchResponse } from '@lio/api-types';
import { ArrayItem } from '@utils/commonTypes';

export type PolicySearchItem = ArrayItem<PolicySearchResponse['items']>;

export type Policy = {
  name: string;
  id: string;
  address: string;
  state: string;
  city: string;
  zipcode: string;
  product: string;
  policy: string;
};

type Status = PolicyGetResponse['status'];
export type PolicyAddress = ArrayItem<PolicyGetResponse['addresses']>;

export const PolicyStatus: Record<Status, string> = {
  Active: 'Active',
  PendingCancelation: 'PendingCancelation',
  Canceled: 'Canceled',
  Expired: 'Expired',
  Rewritten: 'Rewritten',
};

export const PolicyStatusText = {
  [PolicyStatus.Active]: 'Active',
  [PolicyStatus.PendingCancelation]: 'Pending Cancellation',
  [PolicyStatus.Canceled]: 'Cancelled',
  [PolicyStatus.Expired]: 'Expired',
  [PolicyStatus.Rewritten]: 'Rewritten',
};

export enum PolicyType {
  Active = 'Active',
  Inactive = 'Inactive',
}

export enum PolicySearchQueryParamsKey {
  CurrentPage = 'currentPage',
  PageOffset = 'pageOffset',
  PageLimit = 'pageLimit',
  Brokerage = 'brokerageId',
  Customer = 'customerId',
  Underwriter = 'underwriterId',
  Status = 'status',
  SearchText = 'searchText',
  Sort = 'sort',
  ExpiresOnOrBefore = 'expiresOnMax',
  RenewalStatus = 'renewalStatus',
  WritingCompany = 'writingCompany',
  UpdatedDate = 'updatedDate',
  EffectiveDate = 'effectiveDate',
}

export enum PolicySearchField {
  OpenSubjectivities = 'openSubjectivities',
  PendingBORTransfer = 'pendingBORTransfer',
  PolicyId = 'policyId',
  PolicyNumber = 'policyNumber',
  Account = 'account',
  Product = 'product',
  ProductLabel = 'productLabel',
  ProductOption = 'productOption',
  PackageLabel = 'packageLabel',
  Status = 'status',
  CurrentTerm = 'currentTerm',
  RenewalStatus = 'renewalStatus',
  TotalPremium = 'totalPremium',
  Modified = 'modified',
  Brokerage = 'brokerage',
  Producer = 'producer',
  Underwriter = 'underwriter',
  Menu = 'menu',
  PolicyCost = 'policyCost',
  WritingCompany = 'writingCompany',
  UpdatedDate = 'updatedDate',
  EffectiveDate = 'effectiveDate',
  ComplianceReviewDate = 'complianceReviewDate',
  QuoteSource = 'quoteSource',
  QuoteOrigin = 'quoteOrigin',
}

export enum ColumnType {
  Action = 'actions',
  String = 'string',
  Number = 'number',
  Date = 'date',
}
export const SortPolicyQueryParam: Record<string, (order: string) => string> = {
  [PolicySearchField.PolicyId]: (order) => `${order}id`,
  [PolicySearchField.PolicyNumber]: (order) => `${order}policyNumber.policyNumber`,
  [PolicySearchField.Account]: (order) => `${order}customer.name`,
  [PolicySearchField.ProductLabel]: (order) => `${order}boundQuote.productLabel.name`,
  [PolicySearchField.ProductOption]: (order) => `${order}boundQuote.productOption.name`,
  [PolicySearchField.PackageLabel]: (order) => `${order}packageLabel`,
  [PolicySearchField.Status]: (order) => `${order}status`,
  [PolicySearchField.QuoteSource]: (order) => `${order}reportingQuoteSource`,
  [PolicySearchField.Modified]: (order) => `${order}updatedAt`,
  [PolicySearchField.CurrentTerm]: (order) => `${order}effectiveOn`,
  [PolicySearchField.RenewalStatus]: (order) => `${order}renewalStatus`,
  [PolicySearchField.Product]: (order) => `${order}boundQuote.offering.insuranceProduct.name`,
  [PolicySearchField.TotalPremium]: (order) => `${order}boundQuote.totalPremium`,
  [PolicySearchField.PolicyCost]: (order) => `${order}policyCost`,
  [PolicySearchField.Brokerage]: (order) => `${order}brokerage.name`,
  [PolicySearchField.Underwriter]: (order) => `${order}boundQuote.underwriter.user.lastName`,
  [PolicySearchField.Producer]: (order) => `${order}boundQuote.brokerageProducer.user.lastName`,
  // LIO-29798: sorting intentionally continues to key off the underlying writingCompanyId,
  // not the displayed issuing-company override — see the WritingCompany column comment
  // in PolicyDataGrid.constants.tsx.
  [PolicySearchField.WritingCompany]: (order) => `${order}boundQuote.writingCompany.name`,
};
